import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, List, Typography, Space, Collapse, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';

// Checklist base items
const checkListBase = [
  { text: 'Inspection', completed: '' },
  { text: 'Quote', completed: '' },
  { text: 'Complete', completed: '' },
  { text: 'Invoice', completed: '' },
];

// Helper to normalize checklist status
const normalizeChecklistStatus = (value) => {
  if (value === 'started') return 'started';
  if (value === 'done' || value === true || value === 'true') return 'done';
  return '';
};

const getNextChecklistStatus = (value) => {
  const current = normalizeChecklistStatus(value);
  if (current === '') return 'started';
  if (current === 'started') return 'done';
  return '';
};

export default function TradeJobChecklistExecutionView({
  value,
  onChange,
  readOnly = false,
  style,
  className,
}) {
  // Checklist state
  const [checkList, setCheckList] = useState(value || checkListBase);
  const [inputValue, setInputValue] = useState('');

  // Sync with parent
  useEffect(() => {
    if (value) setCheckList(value);
  }, [value]);

  // Notify parent on change
  useEffect(() => {
    if (onChange) onChange(checkList);
    // eslint-disable-next-line
  }, [checkList]);

  // Add checklist item
  const addChecklistItem = useCallback(() => {
    const text = inputValue.trim();
    if (!text) return;
    setCheckList(prev => [...prev, { text, completed: '' }]);
    setInputValue('');
  }, [inputValue]);

  // Toggle checklist item status
  const toggleChecklistItem = useCallback((index) => {
    if (readOnly) return;
    setCheckList(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        completed: getNextChecklistStatus(updated[index].completed),
      };
      return updated;
    });
  }, [readOnly]);

  // Expand/collapse checklist item (for notes)
  const expandChecklistItem = useCallback((index) => {
    setCheckList(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        expanded: !updated[index].expanded,
      };
      return updated;
    });
  }, []);

  // Update checklist note
  const updateChecklistNote = useCallback((index, text) => {
    setCheckList(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        notes: text,
      };
      return updated;
    });
  }, []);

  return (
    <div style={style} className={className}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>Checklist</Typography.Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        {!readOnly && (
          <Input.Search
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onSearch={addChecklistItem}
            placeholder="Add checklist item and press Enter"
            enterButton="Add"
            allowClear
            style={{ maxWidth: 400 }}
          />
        )}
        <List
          bordered
          dataSource={checkList}
          renderItem={(item, index) => (
            <List.Item
              style={{ background: item.completed === 'done' ? '#e6ffed' : undefined }}
              actions={[
                <Button
                  size="small"
                  type={item.completed === 'done' ? 'primary' : 'default'}
                  onClick={() => toggleChecklistItem(index)}
                  disabled={readOnly}
                >
                  {item.completed === 'done' ? 'Done' : item.completed === 'started' ? 'Started' : 'Mark'}
                </Button>,
                <Button
                  size="small"
                  onClick={() => expandChecklistItem(index)}
                  disabled={readOnly}
                >
                  {item.expanded ? 'Hide Note' : 'Add Note'}
                </Button>,
              ]}
            >
              <div>
                <Typography.Text strong>{item.text}</Typography.Text>
                {item.completed && (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    [{item.completed}]
                  </Typography.Text>
                )}
                {item.expanded && (
                  <div style={{ marginTop: 8 }}>
                    <Input.TextArea
                      rows={2}
                      value={item.notes || ''}
                      onChange={e => updateChecklistNote(index, e.target.value)}
                      placeholder="Add notes..."
                      disabled={readOnly}
                    />
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
      </Space>
    </div>
  );
}
